import { Link } from 'react-router-dom';
import { FolderOpen, ArrowRight } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { HealthScoreRing } from '@/components/shared/health-score-ring';
import { SeverityBadge } from '@/components/dashboard/severity-badge';
import { cn } from '@/lib/utils';
import type { FindingCounts } from '@nirvik/shared';

export interface PortfolioCardData {
  portfolioId: string;
  name: string;
  description: string | null;
  isDefault: boolean;
  projectCount: number;
  healthScore: number | null;
  findingCounts: FindingCounts;
}

interface Props {
  orgId: string;
  portfolios: PortfolioCardData[];
}

/**
 * Org-dashboard-only section. Renders one card per portfolio with name,
 * default badge, project count, severity-tinted health score, and a
 * compact severity breakdown. Click → portfolio dashboard. The portfolios
 * page (`/orgs/:orgId/portfolios`) stays as the simpler full list.
 */
export function PortfoliosSection({ orgId, portfolios }: Props) {
  if (portfolios.length === 0) return null;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-bold text-foreground uppercase tracking-wide">Portfolios</h2>
        <Link
          to={`/orgs/${orgId}/portfolios`}
          className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 flex items-center gap-1"
        >
          Manage <ArrowRight className="h-3 w-3" />
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {portfolios.map((pf) => {
          const score = pf.healthScore;
          const accent = score === null
            ? 'border-gray-200/60 dark:border-border/40 from-gray-500/5 to-gray-600/5 dark:from-gray-500/10 dark:to-gray-600/10'
            : score >= 80
            ? 'border-green-200/60 dark:border-green-500/30 from-green-500/8 to-emerald-600/4 dark:from-green-500/12 dark:to-emerald-600/8'
            : score >= 50
            ? 'border-yellow-200/60 dark:border-yellow-500/30 from-yellow-500/8 to-orange-500/4 dark:from-yellow-500/12 dark:to-orange-500/8'
            : 'border-red-200/60 dark:border-red-500/30 from-red-500/8 to-orange-500/4 dark:from-red-500/12 dark:to-orange-500/8';

          return (
            <Link key={pf.portfolioId} to={`/orgs/${orgId}/portfolios/${pf.portfolioId}`} className="block group">
              <Card className={cn('p-5 hover:shadow-xl dark:hover:shadow-gray-900/60 transition-all hover:-translate-y-0.5 cursor-pointer overflow-hidden bg-gradient-to-br border', accent)}>
                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-xl bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center shrink-0">
                    <FolderOpen className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-base font-bold text-foreground truncate">{pf.name}</h3>
                      {pf.isDefault && (
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                          Default
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {pf.projectCount} project{pf.projectCount !== 1 ? 's' : ''}
                    </p>
                  </div>

                  {/* Compact health score — sized to fit alongside the
                      title/description without crowding it. The ring's
                      score number + sublabel scale automatically with
                      `size`, so `size < 90` triggers the compact variant
                      that drops the descriptive label. */}
                  {score !== null && (
                    <div className="shrink-0">
                      <HealthScoreRing score={score} size={72} strokeWidth={5} />
                    </div>
                  )}
                </div>

                {pf.findingCounts.total > 0 ? (
                  <div className="flex flex-wrap gap-1.5 mt-4">
                    {(['critical', 'high', 'medium', 'low'] as const)
                      .map((sev) => ({ sev, count: pf.findingCounts[sev] ?? 0 }))
                      .filter(({ count }) => count > 0)
                      .map(({ sev, count }) => (
                        <SeverityBadge key={sev} severity={sev} count={count} />
                      ))}
                  </div>
                ) : pf.projectCount > 0 ? (
                  <p className="text-xs text-muted-foreground mt-4">No findings yet</p>
                ) : (
                  <p className="text-xs text-muted-foreground mt-4">Empty — add a project to start scanning</p>
                )}
              </Card>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
